import { WidgetTypes } from './widget-types';
import { TELECONNECTION_WIDGET_DESCRIPTION } from './teleconnection-widget.constants';
import type { MatDialogConfig } from '@angular/material/dialog';
import type {
  LoadRegion,
  WidgetCard,
  WidgetItemsConfig,
  WidgetPositionConfig,
} from '../interfaces';
import {
  NEWS_FEED_MULTIPLE_PERMISSIONS,
  PERIOD_MAPS_HEADLINES_MULTIPLE_PERMISSIONS,
} from '@firebird-web/acl';

const defaultConfigs = {
  width: '601px',
  height: '428px',
  panelClass: 'custom-dialog-styling',
  autoFocus: false,
};

export const widgetConfigs: Record<WidgetTypes, MatDialogConfig> = {
  [WidgetTypes.heatMap]: {
    ...defaultConfigs,
    width: '584px',
    height: '526px',
  },
  [WidgetTypes.interactiveMap]: {
    ...defaultConfigs,
  },
  [WidgetTypes.demand]: {
    ...defaultConfigs,
    width: 'auto',
  },
  [WidgetTypes.renewables]: {
    ...defaultConfigs,
    width: 'fit-content',
    height: '250px',
  },
  [WidgetTypes.naRenewables]: {
    ...defaultConfigs,
    height: '250px',
  },
  [WidgetTypes.weatherTimeline]: {
    ...defaultConfigs,
  },
  [WidgetTypes.compMatrix]: {
    ...defaultConfigs,
  },
  [WidgetTypes.model]: {
    ...defaultConfigs,
    width: '571px',
    height: '350px',
  },
  [WidgetTypes.weightedGraph]: {
    ...defaultConfigs,
    width: '571px',
    height: '350px',
  },
  [WidgetTypes.weightedGraphDifferences]: {
    ...defaultConfigs,
    width: 'fit-content',
    height: '350px',
  },
  [WidgetTypes.analysis]: {
    ...defaultConfigs,
    width: '479px',
    height: '285px',
  },
  [WidgetTypes.weighted]: {
    ...defaultConfigs,
    width: '479x',
    height: '288px',
  },
  [WidgetTypes.blurb]: {
    ...defaultConfigs,
  },
  [WidgetTypes.periodMapsHeadlines]: {
    ...defaultConfigs,
    width: 'fit-content',
  },
  [WidgetTypes.ec46WeightedDDGraph]: {
    ...defaultConfigs,
    width: '350px',
    height: '320px',
  },
  [WidgetTypes.blog]: {
    ...defaultConfigs,
    width: '422px',
    height: '410px',
  },
  [WidgetTypes.teleconnection]: {
    ...defaultConfigs,
    width: '380px',
    height: '360px',
  },
};

export const WidgetCardData: WidgetCard[] = [
  {
    title: 'Summary Table',
    description:
      'Bring the classic AG2 Home Page forecast table into a widget. Same functionality available but in widget form',
    icon: 'grid_on',
    type: WidgetTypes.heatMap,
  },
  {
    title: 'Interactive Map',
    description:
      'Monitor real-time weather around the globe.  Same layers as offered on the full-size map are also available in widget form',
    icon: 'map',
    type: WidgetTypes.interactiveMap,
  },
  {
    title: 'Renewables',
    description:
      'Visualize Solar and Wind Power forecasts in graphical format for countries across Europe based on data from the ECMWF and GFS models',
    icon: 'light_mode',
    permission: ['Renewables'],
    type: WidgetTypes.renewables,
  },
  {
    title: 'Model Graphics',
    description:
      'Visualize and monitor in real-time, graphics from the key weather models impacting the markets',
    icon: 'public',
    type: WidgetTypes.model,
  },
  {
    title: 'Forecast Analysis',
    description:
      'Visualize and Monitor in real-time the weather model forecasts alongside the AG2 forecast for individual locations in tabular format',
    icon: 'manage_search',
    permission: ['Location_Page', 'Forecast_Analysis'],
    type: WidgetTypes.analysis,
  },
  // {
  //   title: 'Weighted Forecast Graph',
  //   description:
  //     'Visualize and monitor in real-time, the latest AG2 and weather model forecasts in graphical format for a specified region/country',
  //   icon: 'grid_on',
  //   permission: ['Weighted_Forecasts'],
  //   type: WidgetTypes.weightedGraph,
  // },
  {
    title: 'Weighted Forecast Graph',
    description:
      'Visualize and monitor in real-time, the latest AG2 and weather model forecasts and differences in graphical format for a specified region/country',
    icon: 'balance',
    permission: ['Weighted_Forecasts'],
    type: WidgetTypes.weightedGraphDifferences,
  },
  {
    title: 'Weighted Forecast Table',
    description:
      'Visualize and monitor in real-time, the latest AG2 and weather model forecasts in tabular format for a specified region/country',
    icon: 'grid_on',
    permission: ['Weighted_Forecasts'],
    type: WidgetTypes.weighted,
  },
  {
    title: 'Power Demand (Load)',
    description:
      'Visualize and monitor in real-time, the latest AG2 and RTO power demand forecasts alongside the latest observations',
    icon: 'electric_bolt',
    type: WidgetTypes.demand,
    permission: ['Load_Forecasts'],
  },
  {
    title: 'Blurb',
    description:
      'Monitor the latest short status insights (Blurbs) from the AG2 Meteorology Team to ensure you don’t miss any key information',
    icon: 'article',
    type: WidgetTypes.blurb,
    permission: ['Forecaster_Blurbs'],
  },
  {
    title: 'Period Maps and Headlines',
    description:
      'Bring the AG2 1-5, 6-10, 11-15 & Seasonal Forecasts and associated headlines into a widget.',
    icon: 'assignment_globe',
    permission: PERIOD_MAPS_HEADLINES_MULTIPLE_PERMISSIONS,
    type: WidgetTypes.periodMapsHeadlines,
  },
  {
    title: 'EC 46 Weighted Graph',
    description:
      'Visualize and monitor in real-time, the latest EC46 North America DD forecast and differences for a specified region',
    icon: 'balance',
    permission: ['Sub-Seasonal_Forecasts'],
    type: WidgetTypes.ec46WeightedDDGraph,
  },
  {
    title: 'Blog',
    description:
      'Monitor the latest Blogs issued by the AG2 Meteorology team, where they provide a deeper discussion on weather topics impacting the global markets',
    icon: 'newsmode',
    iconFontSet: 'material-symbols-outlined',
    permission: NEWS_FEED_MULTIPLE_PERMISSIONS,
    type: WidgetTypes.blog,
  },
  {
    title: 'Teleconnections',
    description: TELECONNECTION_WIDGET_DESCRIPTION,
    icon: 'timeline',
    permission: ['Teleconnections'],
    type: WidgetTypes.teleconnection,
  },
];

export const CommonWidgetPosition: WidgetPositionConfig = {
  x: 0,
  y: 0,
  h: 0,
  w: 0,
};

export const CommonWidgetItems: WidgetItemsConfig = {
  minItemCols: 2,
  maxItemCols: 5,
};

export const defaultDemandLocation: LoadRegion = {
  locationId: 'ERCOT_TOTAL',
  displayName: 'ERCOT RTO',
  subZones: [
    { locationId: 'ERCOT_TOTAL', displayName: 'ERCOT RTO', subZones: null },
    { locationId: 'ERCOT_COAST', displayName: 'Coast', subZones: null },
    {
      locationId: 'ERCOT_SOUTHCENTRAL',
      displayName: 'South Central',
      subZones: null,
    },
    { locationId: 'ERCOT_EAST', displayName: 'East', subZones: null },
    { locationId: 'ERCOT_NORTH', displayName: 'North', subZones: null },
    {
      locationId: 'ERCOT_NORTHCENTRAL',
      displayName: 'North Central',
      subZones: null,
    },
    { locationId: 'ERCOT_WEST', displayName: 'West', subZones: null },
    { locationId: 'ERCOT_SOUTH', displayName: 'South', subZones: null },
    { locationId: 'ERCOT_FARWEST', displayName: 'Far West', subZones: null },
  ],
};
